import { useMemo, useState } from "react";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { LoadingPulse } from "../../ui";
import {
  collapseScheduleChangeRows,
  groupCollapsedChangesByEmployee,
  slotNeedsPendingReviewForUser,
  viewerChangeReviewStatus,
} from "../../lib/scheduleUnseenChanges";
import { resolveEmployeeName } from "./scheduleHelpers";
import {
  formatScheduleChangeTime,
  scheduleChangeRowDescription,
  scheduleDayKeyToShort,
  ScheduleSectionCollapsedHeader,
} from "./scheduleComponents";

async function acknowledgeChangeIds(ids, userName) {
  const unique = [...new Set((ids || []).filter((id) => id != null))];
  if (!unique.length) return { updated: 0 };
  const res = await fetch(`${API_BASE}/schedule/acknowledge-changes`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ change_ids: unique, hr_user_name: userName }),
  });
  if (!res.ok) throw new Error("acknowledge_failed");
  return res.json();
}

function statusBadge(status, lang) {
  const st = String(status || "").toLowerCase();
  if (st === "own") {
    return { label: lang === "gr" ? "Δική σας" : "Yours", bg: "#E5E7EB", color: "#374151" };
  }
  if (st === "sent") {
    return { label: lang === "gr" ? "Στάλθηκε" : "Sent", bg: "#DBEAFE", color: "#1D4ED8" };
  }
  if (st === "confirmed") {
    return { label: lang === "gr" ? "Επιβεβαιώθηκε" : "Confirmed", bg: "#DCFCE7", color: "#15803D" };
  }
  return { label: lang === "gr" ? "Εκκρεμεί" : "Pending", bg: "#FEF3C7", color: "#92400E" };
}

function markButtonStyle(disabled) {
  return {
    flexShrink: 0,
    border: "1.5px solid var(--green)",
    background: "transparent",
    color: "var(--green)",
    borderRadius: 8,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

export function WeeklyChangesPanel({
  lang,
  user,
  liveStaff,
  weekDates,
  scheduleChangeRows,
  scheduleChangesLoading,
  open,
  onToggle,
  onReload,
  onOpenSlot,
}) {
  const [busyKey, setBusyKey] = useState("");
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const weekIsos = useMemo(
    () => (weekDates || []).map((d) => (typeof d === "string" ? String(d).slice(0, 10) : "")).filter(Boolean),
    [weekDates]
  );

  const grouped = useMemo(
    () => groupCollapsedChangesByEmployee(collapseScheduleChangeRows(scheduleChangeRows, weekIsos), user),
    [scheduleChangeRows, weekIsos, user]
  );
  const pendingSlots = grouped.flatMap((g) => g.slots.filter((s) => slotNeedsPendingReviewForUser(s, user)));
  const pendingIds = grouped.flatMap((g) => g.pendingIds);
  const allConfirmed = grouped.length > 0 && pendingSlots.length === 0;

  const runAck = async (key, ids) => {
    if (!ids?.length || busyKey) return;
    setBusyKey(key);
    try {
      await acknowledgeChangeIds(ids, user?.name);
      onReload?.();
    } catch {
      /* keep pending; user can retry */
    } finally {
      setBusyKey("");
    }
  };

  const summary = scheduleChangesLoading
    ? t("Loading changes…", "Φόρτωση αλλαγών…")
    : grouped.length === 0
      ? t("No changes this week", "Καμία αλλαγή αυτή την εβδομάδα")
      : pendingSlots.length > 0
        ? t(`${pendingSlots.length} slots pending review`, `${pendingSlots.length} slots εκκρεμούν`)
        : allConfirmed
          ? t("All reviewed", "Όλα επιβεβαιωμένα")
          : t(`${grouped.length} people with changes`, `${grouped.length} άτομα με αλλαγές`);

  if (!open) {
    return (
      <ScheduleSectionCollapsedHeader
        badge="CHANGES"
        summary={summary}
        actionLabel={t("Show changes ▾", "Εμφάνιση αλλαγών ▾")}
        onExpand={onToggle}
      />
    );
  }

  return (
    <div style={{ marginTop: 14, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
      <div
        onClick={onToggle}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", flexWrap: "wrap", cursor: "pointer", background: "var(--surface2)", borderBottom: "1px solid var(--border)" }}
      >
        <span style={{ background: "var(--header-dark)", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 4, letterSpacing: 1 }}>
          CHANGES
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
          {t("Weekly Changes", "Αλλαγές Εβδομάδας")}
        </span>
        {pendingIds.length > 0 ? (
          <button
            type="button"
            disabled={Boolean(busyKey)}
            onClick={(e) => {
              e.stopPropagation();
              runAck("week", pendingIds);
            }}
            style={markButtonStyle(Boolean(busyKey))}
          >
            {busyKey === "week" ? t("Saving…", "Αποθήκευση…") : t("Mark week seen", "Σήμανση εβδομάδας")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          style={{ marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, color: "var(--green)", fontWeight: 700, padding: "4px 8px" }}
        >
          {t("Hide", "Απόκρυψη")}
        </button>
      </div>
      <div style={{ padding: "12px 14px 14px" }}>
        {scheduleChangesLoading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-muted)", fontSize: 13 }}>
            <LoadingPulse size={18} />
            {t("Loading…", "Φόρτωση…")}
          </div>
        ) : grouped.length === 0 ? (
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)" }}>
            {t("No changes this week", "Καμία αλλαγή αυτή την εβδομάδα")}
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: pendingSlots.length ? "var(--warning, #F59E0B)" : "var(--green)",
                marginBottom: 10,
              }}
            >
              {summary}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {grouped.map((group) => {
                const empNm = resolveEmployeeName(group.employee_name, liveStaff || []) || group.employee_code || "—";
                const empKey = `emp:${group.employee_code || empNm}`;
                return (
                  <div key={group.employee_code || empNm} style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        padding: "8px 10px",
                        background: "var(--surface2)",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>{empNm}</span>
                      {group.pendingCount > 0 ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#B45309" }}>
                          {t(`${group.pendingCount} to review`, `${group.pendingCount} προς έλεγχο`)}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--green)" }}>{t("Reviewed", "Επιβεβαιωμένο")}</span>
                      )}
                      {group.pendingIds.length > 0 ? (
                        <button
                          type="button"
                          disabled={Boolean(busyKey)}
                          onClick={() => runAck(empKey, group.pendingIds)}
                          style={{ ...markButtonStyle(Boolean(busyKey)), marginLeft: "auto" }}
                        >
                          {busyKey === empKey ? t("Saving…", "Αποθήκευση…") : t("Mark person seen", "Σήμανση ατόμου")}
                        </button>
                      ) : null}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10 }}>
                      {group.slots.map((slot) => {
                        const reviewStatus = viewerChangeReviewStatus(slot, user);
                        const badge = statusBadge(reviewStatus, lang);
                        const desc = scheduleChangeRowDescription(slot, lang);
                        const by = slot.changed_by_name || "—";
                        const needsReview = slotNeedsPendingReviewForUser(slot, user);
                        return (
                          <div
                            key={slot.key}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              flexWrap: "wrap",
                              padding: "8px 10px",
                              borderRadius: 10,
                              background: "var(--surface2)",
                              border: "1px solid var(--border)",
                              fontSize: 12,
                            }}
                          >
                            <span
                              style={{
                                flexShrink: 0,
                                fontSize: 10,
                                fontWeight: 800,
                                padding: "3px 8px",
                                borderRadius: 999,
                                background: badge.bg,
                                color: badge.color,
                              }}
                            >
                              {badge.label}
                            </span>
                            <button
                              type="button"
                              onClick={() => onOpenSlot?.(slot)}
                              style={{
                                flex: "1 1 160px",
                                minWidth: 0,
                                color: "var(--text-primary)",
                                fontWeight: 600,
                                background: "none",
                                border: "none",
                                padding: 0,
                                textAlign: "left",
                                cursor: onOpenSlot ? "pointer" : "default",
                              }}
                            >
                              {scheduleDayKeyToShort(slot.day_key, lang)} — {desc}
                              {slot.count > 1 ? (
                                <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
                                  {lang === "gr" ? ` · ${slot.count} αποθηκεύσεις` : ` · ${slot.count} saves`}
                                </span>
                              ) : null}
                            </button>
                            <span style={{ flexShrink: 0, color: "var(--text-muted)", fontSize: 11, fontWeight: 600 }}>
                              {formatScheduleChangeTime(slot.changed_at)}{" "}
                              <span style={{ fontWeight: 500 }}>{lang === "gr" ? `από ${by}` : `by ${by}`}</span>
                            </span>
                            {needsReview ? (
                              <button
                                type="button"
                                disabled={Boolean(busyKey)}
                                onClick={() => runAck(slot.key, slot.ids)}
                                style={markButtonStyle(Boolean(busyKey))}
                              >
                                {busyKey === slot.key ? t("Saving…", "Αποθήκευση…") : t("Mark seen", "Το είδα")}
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PendingReviewMarker({ lang, visible, onClick }) {
  if (!visible) return null;
  const title = lang === "gr" ? "Εκκρεμεί έλεγχος αλλαγής" : "Pending change to review";
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      style={{
        position: "absolute",
        top: 3,
        right: 4,
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: "#FBBF24",
        border: "1px solid #fff",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
        padding: 0,
        cursor: "pointer",
        zIndex: 4,
      }}
    />
  );
}
